// Per-eid render overrides scoped to a single battle. The Framer
// Motion animation driver writes here to bend a card's display for the
// lifetime of one transition (face_down=false for a sprung trap before
// the move animation, the per-eid `current_attack` / `remaining_life`
// preview for Rally/Reinforce target picks). Card-rendering sites
// (HandRow, Field, pile ghosts) read these and apply them when
// building the <Card> props.

import { createContext, useContext } from "react";
import type { CardView } from "../api/types";

export interface CardOverrides {
  /** Per-eid CardView patches. Used by trap-flip (face_down=false
   *  applied before the rotateY plays out) and by Rally/Reinforce
   *  pick preview (current_attack / remaining_life what-ifs from the
   *  engine's `preview_attackers` / `preview_blocks` query). */
  snapshot: ReadonlyMap<number, Partial<CardView>>;
  /** Eids whose `layoutId` must NOT be set at this site — because a
   *  floating clone elsewhere (the play-hover overlay, a drag clone)
   *  is holding the same layoutId and Motion would refuse to FLIP
   *  cleanly if it saw two matching elements at once. */
  suppress: ReadonlySet<number>;
  /** Eids whose Field tile should run the trap-flip rotateY keyframe
   *  for this beat. Battlefield card sites consume this; everyone
   *  else ignores it. */
  flipping: ReadonlySet<number>;
}

const EMPTY: CardOverrides = {
  snapshot: new Map(),
  suppress: new Set(),
  flipping: new Set(),
};

const CardOverridesContext = createContext<CardOverrides>(EMPTY);

export const CardOverridesProvider = CardOverridesContext.Provider;

export function useCardOverrides(): CardOverrides {
  return useContext(CardOverridesContext);
}

/** The Framer Motion `layoutId` for a given card, or `undefined` when
 *  the site should not participate in layout transitions (because
 *  another element is holding the id). */
export function effectiveLayoutId(
  entityId: number,
  overrides: CardOverrides,
): string | undefined {
  if (overrides.suppress.has(entityId)) return undefined;
  return `card-${entityId}`;
}

export function patchCardView(
  card: CardView,
  overrides: CardOverrides,
): CardView {
  const patch = overrides.snapshot.get(card.entity_id);
  if (!patch) return card;
  return { ...card, ...patch };
}
